import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  ButtonBase,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { updatepageName } from '../../Redux/page';
import LoyaltyIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { GROWTH_FONT, GROWTH_PINK } from './GrowthModal';
import PushNotificationPanel from './PushNotificationPanel';

/**
 * Shared catalog for Growth / Engagement admin features.
 * Hub landing cards navigate to each feature page.
 */
export const GROWTH_FEATURE_CARDS = [
  {
    id: 'loyalty_config',
    title: 'Loyalty Config',
    subtitle: 'Earn rates, welcome bonus & redemption rules per hub',
    path: '/home/loyalty_config',
    pageName: 'loyalty_config',
    group: 'Loyalty',
    icon: LoyaltyIcon,
    accent: '#ED1E79',
    soft: '#FDE9F2',
    border: '#F8A5C9',
  },
  {
    id: 'user_loyalty',
    title: 'User Loyalty',
    subtitle: 'Look up wallets, balances and point history by user',
    path: '/home/user_loyalty',
    pageName: 'user_loyalty',
    group: 'Loyalty',
    icon: GroupsOutlinedIcon,
    accent: '#C2185B',
    soft: '#FCE4EC',
    border: '#F48FB1',
  },
  {
    id: 'referral_config',
    title: 'Referral Program',
    subtitle: 'Referrer rewards, join bonus & signup / first-order triggers',
    path: '/home/referral_config',
    pageName: 'referral_config',
    group: 'Referral',
    icon: PeopleAltOutlinedIcon,
    accent: '#0071BC',
    soft: '#E3F2FD',
    border: '#90CAF9',
  },
  {
    id: 'wallet_topup_bonus',
    title: 'Wallet Top-up Bonus',
    subtitle: 'Hub top-up % / fixed bonus slabs for cash wallet',
    path: '/home/wallet_topup_bonus',
    pageName: 'wallet_topup_bonus',
    group: 'Wallet',
    icon: AccountBalanceWalletOutlinedIcon,
    accent: '#00897B',
    soft: '#E0F2F1',
    border: '#80CBC4',
  },
  {
    id: 'bonus_grants',
    title: 'Wallet Bonus Grants',
    /*
     * Named "bonus", not "wallet", because it is a different pot. The cash wallet
     * holds money the customer topped up and is owed back; this holds promotional
     * credit that expires and is spent first.
     */
    subtitle: 'Grant expiring promo credit by hub, subscription state or order history',
    path: '/home/bonus_grants',
    pageName: 'bonus_grants',
    group: 'Wallet',
    icon: CardGiftcardOutlinedIcon,
    accent: '#AD1457',
    soft: '#FCE4EC',
    border: '#F48FB1',
  },
  {
    id: 'wallet_checkout_config',
    title: 'Wallet Checkout',
    subtitle: 'Partial / full wallet pay options on mobile checkout',
    path: '/home/wallet_checkout_config',
    pageName: 'wallet_checkout_config',
    group: 'Wallet',
    icon: PaymentsOutlinedIcon,
    accent: '#00695C',
    soft: '#E8F5E9',
    border: '#A5D6A7',
  },
  {
    /*
     * The backend for this shipped with migration 0117 and had no tile, no
     * route and no screen — so the only way to set a platform fee was Django
     * admin. A feature nobody can reach from the product is a feature that is
     * off, whatever the database says.
     */
    id: 'platform_fee_config',
    title: 'Platform Fee',
    subtitle: 'Flat per-order charge, set per hub',
    path: '/home/platform_fee_config',
    pageName: 'platform_fee_config',
    group: 'Wallet',
    icon: ReceiptLongOutlinedIcon,
    accent: '#EF6C00',
    soft: '#FFF3E0',
    border: '#FFCC80',
  },
  {
    id: 'mpin_config',
    title: 'MPIN Config',
    subtitle: 'Quick-unlock PIN policy, inactivity OTP & channel per hub',
    path: '/home/mpin_config',
    pageName: 'mpin_config',
    group: 'Security',
    icon: LockOutlinedIcon,
    accent: '#37474F',
    soft: '#ECEFF1',
    border: '#90A4AE',
  },
  {
    id: 'promo_coupons',
    title: 'Promo Coupons',
    subtitle: 'Cart promo codes — % or fixed discount by hub',
    path: '/home/promo_coupons',
    pageName: 'promo_coupons',
    group: 'Promo',
    icon: LocalOfferOutlinedIcon,
    accent: '#F57C00',
    soft: '#FFF3E0',
    border: '#FFCC80',
  },
  {
    id: 'subscription_plans',
    title: 'Subscription Plans',
    subtitle: 'VIP plan catalog, benefits and pricing',
    path: '/home/subscription_plans',
    pageName: 'subscription_plans',
    group: 'Subscription',
    icon: CardMembershipOutlinedIcon,
    accent: '#6A1B9A',
    soft: '#F3E5F5',
    border: '#CE93D8',
  },
  {
    id: 'user_subscriptions',
    title: 'User Subscriptions',
    subtitle: 'Active / pending member subscriptions',
    path: '/home/user_subscriptions',
    pageName: 'user_subscriptions',
    group: 'Subscription',
    icon: CardGiftcardOutlinedIcon,
    accent: '#7B1FA2',
    soft: '#F8EAFB',
    border: '#E1BEE7',
  },
  {
    id: 'subscription_settings',
    title: 'Subscription Settings',
    subtitle: 'Global subscription behaviour and defaults',
    path: '/home/subscription_settings',
    pageName: 'subscription_settings',
    group: 'Subscription',
    icon: SettingsSuggestOutlinedIcon,
    accent: '#5E35B1',
    soft: '#EDE7F6',
    border: '#B39DDB',
  },
];

export function getGrowthFeatureMenuItems(navigate, dispatch) {
  return [
    {
      label: 'Growth Hub',
      command: () => {
        navigate('/home/growth_hub');
        dispatch(updatepageName('growth_hub'));
      },
    },
  ];
}

const FeatureCard = ({ item, onOpen }) => {
  const Icon = item.icon;
  return (
    <ButtonBase
      onClick={() => onOpen(item)}
      focusRipple
        sx={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          borderRadius: '8px',
          fontFamily: GROWTH_FONT,
          transition: 'transform 160ms ease, box-shadow 160ms ease',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: `0 14px 28px ${item.accent}22`,
          },
          '&:hover .go-arrow': {
            transform: 'translateX(4px)',
            opacity: 1,
          },
        }}
    >
      <Box
        sx={{
          height: '100%',
          minHeight: 168,
          p: 2.25,
          borderRadius: '8px',
          background: `linear-gradient(155deg, ${item.soft} 0%, #FFFFFF 62%)`,
          border: `1px solid ${item.border}`,
          boxShadow: `8px 10px 18px 0px ${item.accent}1A`,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              backgroundColor: '#FFFFFF',
              border: `1px solid ${item.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: item.accent,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Chip
            label={item.group}
            size="small"
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 500,
              backgroundColor: '#FFFFFF',
              color: item.accent,
              border: `1px solid ${item.border}`,
            }}
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontFamily: GROWTH_FONT,
              fontSize: 16,
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.3,
              mb: 0.5,
            }}
          >
            {item.title}
          </Typography>
          <Typography
            sx={{
              fontFamily: GROWTH_FONT,
              fontSize: 12.5,
              color: 'text.secondary',
              lineHeight: 1.45,
            }}
          >
            {item.subtitle}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: item.accent,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Open
          <ArrowForwardRoundedIcon
            className="go-arrow"
            sx={{ fontSize: 18, opacity: 0.85, transition: 'all 160ms ease' }}
          />
        </Box>
      </Box>
    </ButtonBase>
  );
};

/**
 * Growth Features hub — Loyalty, Wallet, Referral, Subscription, Promo.
 * Matches admin theme (Poppins + #ED1E79 accents, soft pink cards).
 */
export function GrowthFeaturesHub() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const grouped = useMemo(() => {
    const map = {};
    GROWTH_FEATURE_CARDS.forEach((card) => {
      if (!map[card.group]) map[card.group] = [];
      map[card.group].push(card);
    });
    return map;
  }, []);

  const openFeature = (item) => {
    navigate(item.path);
    dispatch(updatepageName(item.pageName));
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100%',
        p: { xs: 2, md: 3 },
        backgroundColor: 'background.default',
        fontFamily: GROWTH_FONT,
      }}
    >
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: '20px',
          background:
            'linear-gradient(120deg, #FDE9F2 0%, #FFFFFF 48%, #F0F4FC 100%)',
          border: '1px solid #F8A5C9',
          boxShadow: '10px 10px 20px 0px #ED1E7922',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Chip
            label="Growth & Engagement"
            size="small"
            sx={{
              mb: 1,
              backgroundColor: GROWTH_PINK,
              color: '#fff',
              fontWeight: 500,
              fontFamily: GROWTH_FONT,
            }}
          />
          <Typography
            sx={{
              fontFamily: GROWTH_FONT,
              fontSize: { xs: 22, md: 28 },
              fontWeight: 700,
              color: 'text.primary',
              lineHeight: 1.25,
            }}
          >
            Loyalty · Wallet · Referral · Subscription · Promo
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              fontFamily: GROWTH_FONT,
              fontSize: 14,
              color: 'text.secondary',
              maxWidth: 640,
            }}
          >
            Configure the new growth features from one place. Pick a card to open
            the exact admin page — same routes as before, cleaner navigation.
          </Typography>
        </Box>
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderRadius: '12px',
            backgroundColor: '#FFFFFF',
            border: '1px solid #F8A5C9',
            minWidth: 120,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontFamily: GROWTH_FONT, fontSize: 11, color: 'text.secondary' }}>
            Modules
          </Typography>
          <Typography sx={{ fontFamily: GROWTH_FONT, fontSize: 24, fontWeight: 700, color: GROWTH_PINK }}>
            {GROWTH_FEATURE_CARDS.length}
          </Typography>
        </Box>
      </Box>

      <PushNotificationPanel />

      {Object.entries(grouped).map(([group, cards]) => (
        <Box key={group} sx={{ mb: 3.5 }}>
          <Typography
            sx={{
              fontFamily: GROWTH_FONT,
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              color: 'text.secondary',
              mb: 1.5,
            }}
          >
            {group}
          </Typography>
          <Grid container spacing={2}>
            {cards.map((item) => (
              <Grid item xs={12} sm={6} lg={4} key={item.id}>
                <FeatureCard item={item} onOpen={openFeature} />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
}

export default GrowthFeaturesHub;
